import React, { useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '../../utils/dateUtils';
import { JournalEntry } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const getStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
        case 'DRAFT': return 'warning';
        case 'POSTED': return 'success';
        case 'VOID': return 'error';
        default: return 'default';
    }
};

const JournalEntryDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [entry, setEntry] = useState<JournalEntry | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchEntry = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await JournalEntryService.getEntryById(id);
            setEntry(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load journal entry');
            console.error('Error fetching journal entry:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntry();
    }, [id]);

    const handlePost = async () => {
        if (!id) return;
        try {
            await JournalEntryService.postEntry(id);
            await fetchEntry();
        } catch (err) {
            toast.showApiError(err, 'Failed to post journal entry');
            console.error('Error posting entry:', err);
        }
    };

    const handleVoid = async () => {
        if (!id) return;
        try {
            await JournalEntryService.voidEntry(id);
            await fetchEntry();
        } catch (err) {
            toast.showApiError(err, 'Failed to void journal entry');
            console.error('Error voiding entry:', err);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!entry) {
        return <div style={{ color: 'red', padding: '20px' }}>Journal entry not found</div>;
    }

    const totalDebit = entry.lines?.filter(l => l.entryType === 'DEBIT').reduce((sum, l) => sum + l.amount, 0) || 0;
    const totalCredit = entry.lines?.filter(l => l.entryType === 'CREDIT').reduce((sum, l) => sum + l.amount, 0) || 0;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Journal Entry Detail</h2>
                <Button variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                    Back to List
                </Button>
            </div>

            {/* Entry Header */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <strong>Entry Number:</strong> {entry.entryNumber || '-'}
                    </div>
                    <div>
                        <strong>Date:</strong> {entry.date ? formatDate(entry.date) : '-'}
                    </div>
                    <div>
                        <strong>Description:</strong> {entry.description || '-'}
                    </div>
                    <div>
                        <strong>Status:</strong>{' '}
                        <Chip label={entry.status} color={getStatusColor(entry.status)} size="small" />
                    </div>
                    {entry.referenceType && (
                        <div>
                            <strong>Reference:</strong>{' '}
                            {entry.referenceType === 'SALARY_PAYMENT' ? (
                                // Bidirectional drill-down to the Payments tab of the payroll
                                // requires payrollId on the JournalEntry response (resolving
                                // the payment → payroll on the backend). That enhancement is
                                // deferred; surface the label + id for now.
                                <span>
                                    Salary Payment {entry.referenceId ? `(${entry.referenceId})` : ''}
                                </span>
                            ) : (
                                <span>{entry.referenceType} {entry.referenceId ? `(${entry.referenceId})` : ''}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
                {entry.status === 'DRAFT' && (
                    <Button variant="contained" color="success" onClick={handlePost}>
                        Post Entry
                    </Button>
                )}
                {entry.status !== 'VOID' && (
                    <Button variant="contained" color="error" onClick={handleVoid}>
                        Void Entry
                    </Button>
                )}
            </div>

            {/* Lines Table */}
            <h3 style={{ marginBottom: '12px' }}>Entry Lines</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Account Code</th>
                        <th style={tableHeaderStyle}>Account Name</th>
                        <th style={tableHeaderStyle}>Debit</th>
                        <th style={tableHeaderStyle}>Credit</th>
                        <th style={tableHeaderStyle}>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {entry.lines?.map((line, index) => (
                        <tr key={line.id || index}>
                            <td style={tableCellStyle}>{line.accountCode || '-'}</td>
                            <td style={tableCellStyle}>{line.accountName || '-'}</td>
                            <td style={tableCellStyle}>
                                {line.entryType === 'DEBIT' ? line.amount.toFixed(2) : '-'}
                            </td>
                            <td style={tableCellStyle}>
                                {line.entryType === 'CREDIT' ? line.amount.toFixed(2) : '-'}
                            </td>
                            <td style={tableCellStyle}>{line.description || '-'}</td>
                        </tr>
                    ))}
                    {/* Total Row */}
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        <td style={tableCellStyle} colSpan={2}>Total</td>
                        <td style={tableCellStyle}>{totalDebit.toFixed(2)}</td>
                        <td style={tableCellStyle}>{totalCredit.toFixed(2)}</td>
                        <td style={tableCellStyle}></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default JournalEntryDetail;
